import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';

import { DataSourceProvider as ReferentialityDataSourceProvider } from '@atlassian/editor-referentiality/data-source-provider';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { getConnectDataListener } from '@confluence/app-referentiality/entry-points/connectDataListener.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { createNestedLcmHydrationExtensionHandlers } from '@confluence/content-renderer-legacy-macros/entry-points/createNestedLcmHydrationExtensionHandlers.ts';
import { getSsrExtensionHandlers } from '@confluence/content-renderer-legacy-macros/entry-points/getSsrExtensionHandlers.ts';
import { removeDuplicateResourcesForMacros } from '@confluence/content-renderer-legacy-macros/entry-points/ssrTransformers.ts';
import type { ContentUnifiedQueryMacroRenderedOutputType } from '@confluence/content-unified-query';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { includeSuperbatchForFabricMacro } from '../PageContentRenderer.tsx';

import { createExtensionHandlersWithoutSSR } from './createExtensionHandlersWithoutSSR.ts';
import type { PageContentRendererFabricProps } from './PageContentRendererFabricProps.ts';

const TEAM_CALENDAR_EXTENSION_KEY_REGEXP = new RegExp(
	`"extensionKey"\\s*:\\s*"${EXTENSION_KEY.TEAM_CALENDARS}"`,
);
const LEGACY_CONTENT_EXTENSION_KEY_REGEXP = new RegExp(
	`"extensionKey"\\s*:\\s*"${EXTENSION_KEY.LEGACY_CONTENT}"`,
);

export function useExtensionHandlersForPageContentRendererFabric(
	{
		content,
		contentId,
		contentBody,
		contentMode,
		isTemplateEditorRenderer,
		spaceKey,
		spaceId,
		isPreviewMode,
	}: PageContentRendererFabricProps,
	annotationProvider: ADFRendererProps['annotationProvider'],
): {
	dataProviders: ProviderFactory;
	extensionHandlers: ExtensionHandlers<any> | undefined;
} {
	const { featureFlags, environment, cloudId, userId, locale, activationId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [manifestReadyState, setManifestReadyState] = useState(false);
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	const isLivePage = useIsCurrentPageLive();

	const contentType = content?.type;
	const adf = contentBody?.value || '';
	const memoizedHasTeamCalendarMacro = useMemo(() => {
		if (!expValEquals('cc-proj-xl-memoize-team-calendar-regexp', 'isEnabled', true)) {
			return undefined;
		}

		return TEAM_CALENDAR_EXTENSION_KEY_REGEXP.test(adf);
	}, [adf]);
	const hasTeamCalendarMacro =
		memoizedHasTeamCalendarMacro ?? TEAM_CALENDAR_EXTENSION_KEY_REGEXP.test(adf);
	const hasLegacyContentMacro = useMemo(() => LEGACY_CONTENT_EXTENSION_KEY_REGEXP.test(adf), [adf]);

	const PDF_URL = '/wiki/pdf/';
	const isCurrentLocationPdf = (): boolean => Boolean(window?.location?.href?.includes(PDF_URL));

	const contentStatus = isCurrentLocationPdf() ? content?.status : undefined;

	const macroRenderedOutput = useMemo(
		() =>
			content && 'macroRenderedOutput' in content
				? removeDuplicateResourcesForMacros(
						content['macroRenderedOutput'] as ContentUnifiedQueryMacroRenderedOutputType[],
					)
				: null,
		[content],
	);

	// If legacy fabric macros exist, superbatch css and js needs to be added
	// only once before any macros are rendered.
	const includeSuperbatch = useCallback(() => {
		includeSuperbatchForFabricMacro(contentBody, macroRenderedOutput);
	}, [contentBody, macroRenderedOutput]);

	// Extension handlers during SSR should not be used to populate extension providers
	// Otherwise extensions manifest would reference the wrong handlers if it needs to be overrided
	// during runtime
	const extensionHandlersWithSSR = useMemo(
		() =>
			getSsrExtensionHandlers({
				adf: contentBody?.value,
				contentId: contentId || '',
				contentMode,
				spaceKey,
				cloudId,
				userId,
				macroRenderedOutput,
				includeSuperbatch,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentMode, includeSuperbatch],
	);
	// Only pages that actually contain an LCM can be affected by recursive LCM SSR, so the gate is
	// read behind that check: an LCM-free page must not be counted as exposed to it.
	const nestedLcmHydrationExtensionHandlers = useMemo(
		() =>
			hasLegacyContentMacro && fg('confluence_lcm_ssr_nested')
				? createNestedLcmHydrationExtensionHandlers({
						annotationProvider,
						contentId: contentId || '',
						contentMode,
						// Same macro rendering as the server, but declining the macros the SSR path does
						// not own so they reach their manifests instead of staying empty forever.
						nestedExtensionHandlers: getSsrExtensionHandlers({
							adf: contentBody?.value,
							contentId: contentId || '',
							contentMode,
							spaceKey,
							cloudId,
							userId,
							macroRenderedOutput,
							includeSuperbatch,
							releaseEmptyMacrosToManifest: true,
						}),
						serverExtensionHandlers: extensionHandlersWithSSR,
					})
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			annotationProvider,
			contentId,
			contentMode,
			extensionHandlersWithSSR,
			hasLegacyContentMacro,
			includeSuperbatch,
		],
	);

	const referentialityDataSourceProvider = useMemo(() => {
		const refDataProvider = new ReferentialityDataSourceProvider();

		getConnectDataListener().init({
			emit: (localId, reference) => refDataProvider.createOrUpdate(localId, reference),
		});

		return refDataProvider;
	}, []);

	const extensionHandlers = useMemo(() => {
		return getExtensionHandlers?.(
			{
				adf,
				contentId,
				spaceKey,
				macroRenderedOutput,
				showTemplateVariablesInPreview: isTemplateEditorRenderer,
				shouldIncludeMacroWithReferencesHandlers: true,
				referentialityDataSourceProvider,
				contentType,
				isPreviewMode,
				annotationProvider,
			},
			createAnalyticsEvent,
		) as ADFRendererProps['extensionHandlers'];
	}, [
		getExtensionHandlers,
		contentId,
		spaceKey,
		adf,
		macroRenderedOutput,
		contentType,
		isPreviewMode,
		createAnalyticsEvent,
		isTemplateEditorRenderer,
		annotationProvider,
		referentialityDataSourceProvider,
	]);

	const isSsrRendered = window.__SSR_RENDERED__;
	// The extensionHandlers prop value of PageContentRendererFabric when NOT isTemplateEditorRenderer:
	const handlers: ADFRendererProps['extensionHandlers'] = useMemo(() => {
		if (!isTemplateEditorRenderer) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				// Return extension handlers for supported content only
				return extensionHandlersWithSSR;
			} else if (!manifestReadyState) {
				// POST/NON SSR, macros V2 is on, and manifest is ready, extension handlers is undefined to use extension manifest
				// Don't use client side macro renderer until manifestReadyState because it takes additional cycle to render
				// The loading state will destroy the SSR rendered content
				//
				// The first render of client-side need extensionHandlersWithSSR for proper hydration as well.
				if (isSsrRendered) {
					return extensionHandlersWithSSR;
				} else {
					return createExtensionHandlersWithoutSSR(extensionHandlers);
				}
			} else {
				// After the extension manifest is ready, most macros return undefined so the manifest
				// renderer can take over. Two families stay on the product handler instead: migrated
				// Team Calendar macros, so the sized SSR placeholder survives the manifest-ready
				// handoff, and recursively SSRed LCM hierarchies, so the mounted component tree
				// survives it.
				const lcmHandlers =
					hasLegacyContentMacro && isSsrRendered ? nestedLcmHydrationExtensionHandlers : undefined;
				// Team Calendar remains on the MACRO_CORE handler after manifest readiness, but sibling native
				// embeds must fall through to their manifests or native databases will not render.
				const teamCalendarHandlers =
					hasTeamCalendarMacro &&
					expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
						? createExtensionHandlersWithoutSSR(extensionHandlers, {
								allowNativeEmbedHandling: !expValEquals(
									'cc-team-cal-afm-native-embed-handoff-fix',
									'isEnabled',
									true,
								),
							})
						: undefined;

				// The two sets are keyed by disjoint extension types, so a page containing both macro
				// kinds must keep both rather than letting one shadow the other.
				if (!lcmHandlers || !teamCalendarHandlers) {
					return lcmHandlers ?? teamCalendarHandlers;
				}

				return { ...teamCalendarHandlers, ...lcmHandlers };
			}
		}
	}, [
		extensionHandlers,
		extensionHandlersWithSSR,
		hasLegacyContentMacro,
		hasTeamCalendarMacro,
		isSsrRendered,
		isTemplateEditorRenderer,
		manifestReadyState,
		nestedLcmHydrationExtensionHandlers,
	]);

	const { dataProviders, manifestPreload } = useMemo<{
		dataProviders: ProviderFactory;
		manifestPreload?: Promise<void>;
	}>(
		() => {
			const providerFactory = ProviderFactory.create({});
			const intlStub = {
				formatMessage: (message) => message?.defaultMessage,
				locale,
			};
			let manifestPreload: Promise<void> | undefined;

			if (populateExtensionProviders) {
				// The populator only calls `setManifestReadyState` while
				// confluence_defer_manifest_ready_state is off. With it on, the effect below subscribes to
				// the returned preload instead, so a render React discards cannot flip the state.
				manifestPreload =
					populateExtensionProviders(providerFactory, {
						intl: intlStub,
						containerId: spaceKey,
						accountId: userId,
						cloudId,
						spaceKey,
						spaceId,
						contentId,
						contentType,
						contentVersion: content?.version?.number,
						contentStatus,
						contentMode,
						environment,
						featureFlags,
						extensionHandlers,
						setManifestReadyState,
						createAnalyticsEvent,
						activationId,
						isLivePage,
						hasLegacyContentMacro,
					}) || undefined;
			}

			return { dataProviders: providerFactory, manifestPreload };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			contentMode,
			contentType,
			extensionHandlers,
			populateExtensionProviders,
			createAnalyticsEvent,
			isLivePage,
			hasLegacyContentMacro,
		],
	);

	useEffect(() => {
		if (
			!manifestPreload ||
			!expValEquals('confluence_defer_manifest_ready_state', 'isEnabled', true)
		) {
			return;
		}
		let cancelled = false;
		void manifestPreload.then(
			() => {
				if (!cancelled) {
					setManifestReadyState(true);
				}
			},
			() => undefined,
		);
		return () => {
			cancelled = true;
		};
	}, [manifestPreload]);

	return {
		dataProviders,
		extensionHandlers: isTemplateEditorRenderer ? extensionHandlers : handlers,
	};
}
